import { useMemo } from 'react';
import { type ColumnWebPageBlockDataFragment } from '../../../../../api/fragments/ColumnWebPageBlockData';
import { useThemeComponents } from '../../../../../themes';
import useTranslatedString from '../../../../../utilities/useTranslatedString';
import {
    StyledColumnBlockCardContainer,
    StyledColumnBlockContainer,
    StyledColumnBlockMainContainer,
    StyledColumnTitle,
} from '../shared';
import ColumnCard from './ColumnCard';

type ColumnBlockProps = {
    columnBlock: ColumnWebPageBlockDataFragment;
};

const ColumnBlock = ({ columnBlock }: ColumnBlockProps) => {
    const translated = useTranslatedString();
    const { Carousel } = useThemeComponents();

    const { columns } = columnBlock;
    const total = columns?.length || 0;

    const slidesPerPage = useMemo(
        () => ({
            base: 1,
            xxs: Math.min(1, total),
            xs: Math.min(1, total),
            s: Math.min(2, total),
            xl: Math.min(3, total),
            xxl: Math.min(4, total),
        }),
        [total]
    );

    const disablePagination = useMemo(
        () => ({
            base: true,
            xxs: false,
            xs: false,
            s: total <= slidesPerPage.s,
            xl: total <= slidesPerPage.xl,
            xxl: total <= slidesPerPage.xxl,
        }),
        [slidesPerPage, total]
    );

    if (!columns?.length) {
        return null;
    }

    return (
        <StyledColumnBlockMainContainer>
            <StyledColumnBlockContainer>
                <StyledColumnBlockCardContainer>
                    <Carousel
                        disablePagination={JSON.stringify(disablePagination)}
                        slidesPerPage={JSON.stringify(slidesPerPage)}
                    >
                        <StyledColumnTitle itemProp="alternativeHeadline" slot="heading">
                            {translated(columnBlock.title)}
                        </StyledColumnTitle>
                        <ColumnCard columns={columns} />
                    </Carousel>
                </StyledColumnBlockCardContainer>
            </StyledColumnBlockContainer>
        </StyledColumnBlockMainContainer>
    );
};

export default ColumnBlock;
